import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { sandboxApi } from '@/api/sandbox.api';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { cortexApi, memoryApi, memoryClassApi } from '@/api/cortex.api';
import { ragPluginApi } from '@/api/ragPlugin.api';
import { applicationApi } from '@/api/application.api';
import { mcpApi } from '@/api/mcp.api';
import { mcpClassApi } from '@/api/mcpClass.api';
import { agentApi } from '@/api/agent.api';
import { ChannelType, ChannelAccountBindingMode } from '@/proto/agentplatform/v1/channel_pb';
import { FilesystemTool } from '@/proto/agentplatform/v1/agent_pb';
import {
  PLUGIN_SUB_TYPE_CLASS,
  PLUGIN_SUB_TYPE_INSTANCE,
  PLUGIN_SUB_TYPE_TOOLKIT,
  PLUGIN_SUB_TYPE_USER_TOOLKIT,
  PLUGIN_SUB_TYPE_APPLICATION,
} from '@/constants/plugin';
import { type ParamItem } from '@/components/ParamConfigEditor';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

export const SECTIONS = [
  { id: 'basic', num: 1, title: '基本信息', desc: 'Agent 的名称与描述。命名建议使用 kebab 风格。' },
  {
    id: 'model',
    num: 2,
    title: '模型配置',
    desc: '选择模型凭证（API Key 由凭证托管），再选择推理模型。',
  },
  {
    id: 'filesystem',
    num: 3,
    title: '文件系统',
    desc: '控制是否启用 filesystem middleware，供 Agent 读写工作区文件。',
  },
  {
    id: 'todoList',
    num: 4,
    title: '待办列表',
    desc: '控制是否启用 todo_list middleware 及其 description 引导。',
  },
  {
    id: 'askUser',
    num: 5,
    title: '用户提问',
    desc: '控制是否启用 ask_user middleware，允许 Agent 向用户提问以澄清需求。',
  },
  {
    id: 'webSearch',
    num: 6,
    title: '联网搜索',
    desc: 'web_search middleware（Tavily）：为 Agent 提供 web_search / web_fetch 工具。',
  },
  {
    id: 'currentTime',
    num: 7,
    title: '当前时间',
    desc: 'current_time middleware：每次模型调用时向 Agent 注入当前时间。',
  },
  { id: 'compression', num: 8, title: '压缩配置', desc: '配置上下文压缩以优化长对话性能。' },
  {
    id: 'faultTolerance',
    num: 9,
    title: '容错配置',
    desc: '配置任务级 / 步骤级重试、超时与模型降级切换。',
  },
  {
    id: 'toolPermissions',
    num: 10,
    title: '工具权限',
    desc: 'HITL 审批策略：deny 优先 → 命中即拒；allow 命中即过；无命中需人工审批。',
  },
  {
    id: 'channel',
    num: 11,
    title: '渠道配置',
    desc: '选择需要支持的接入渠道及绑定模式。多渠道按列表叠加。',
  },
  { id: 'plugins', num: 12, title: '插件配置', desc: '为 Agent 添加插件以扩展功能。' },
];

export const MODEL_TARGET_OPTIONS = [
  {
    group: '模型配置',
    options: [
      { value: 'model_config.model_name', label: '模型名称', dataType: 'string' },
      { value: 'model_config.api_key_ref', label: '模型凭证 ID', dataType: 'string' },
      { value: 'model_config.api_key', label: 'API Key', dataType: 'string' },
      { value: 'model_config.system_prompt', label: '系统提示词', dataType: 'string' },
      { value: 'model_config.extra_config.temperature', label: '温度', dataType: 'number' },
      {
        value: 'model_config.extra_config.enable_thinking',
        label: '启用思考',
        dataType: 'boolean',
      },
      {
        value: 'model_config.extra_config.reasoning_effort',
        label: 'thinking level',
        dataType: 'string',
      },
      {
        value: 'model_config.extra_config.presence_penalty',
        label: '存在惩罚',
        dataType: 'number',
      },
      {
        value: 'model_config.extra_config.frequency_penalty',
        label: '频率惩罚',
        dataType: 'number',
      },
    ],
  },
];

export const MODEL_TARGET_TYPE_MAP: Record<string, string> = {};
MODEL_TARGET_OPTIONS.forEach((g) =>
  g.options.forEach((o) => {
    MODEL_TARGET_TYPE_MAP[o.value] = o.dataType;
  })
);

export interface ExtraConfigFormState {
  temperature: number;
  enableThinking: boolean | undefined;
  reasoningEffort: string;
  presencePenalty: number;
  frequencyPenalty: number;
  // 以下两个从模型元数据自动获取，仅展示
  maxOutputLength: bigint;
  contextLength: bigint;
}

export interface CompressionFormState {
  enabled: boolean;
}

/** 容错：步骤类型级策略（对应 proto StepTypePolicy），扁平映射便于表单编辑 */
export interface StepTypePolicyFormState {
  /** 是否勾选此步骤类型 */
  selected: boolean;
  /** 单次尝试超时（秒），≤ 72h（259200） */
  attemptTimeoutSeconds: number;
  /** 是否启用步骤重试 */
  retryEnabled: boolean;
  /** 最大重试次数 1-10 */
  maxRetries: number;
  /** 重试间隔（秒）1-10 */
  retryIntervalSeconds: number;
  /** 仅 MODEL 类型：模型降级切换开关 */
  failoverEnabled: boolean;
  /** 仅 MODEL 类型：备用模型池（有序，模型名称） */
  backupModels: string[];
}

/** 容错配置表单态（对应 proto FaultToleranceConfig，挂 advanced_config.fault_tolerance） */
export interface FaultToleranceFormState {
  /** 容错总开关 */
  enabled: boolean;
  // ---- 任务级容错 ----
  taskEnabled: boolean;
  /** 任务总超时（秒），≤ 168h（604800） */
  taskTotalTimeoutSeconds: number;
  taskRetryEnabled: boolean;
  taskMaxRetries: number;
  taskRetryIntervalSeconds: number;
  // ---- 步骤级容错 ----
  stepEnabled: boolean;
  /** 按步骤类型：MODEL / TOOL */
  model: StepTypePolicyFormState;
  tool: StepTypePolicyFormState;
  /** 是否开放给实例单独配置（整块级） */
  configurable: boolean;
}

function defaultStepPolicy(selected: boolean): StepTypePolicyFormState {
  return {
    selected,
    attemptTimeoutSeconds: 3600,
    retryEnabled: true,
    maxRetries: 3,
    retryIntervalSeconds: 3,
    failoverEnabled: false,
    backupModels: [],
  };
}

export const DEFAULT_FAULT_TOLERANCE: FaultToleranceFormState = {
  enabled: true,
  taskEnabled: false,
  taskTotalTimeoutSeconds: 3600,
  taskRetryEnabled: false,
  taskMaxRetries: 3,
  taskRetryIntervalSeconds: 3,
  stepEnabled: true,
  model: defaultStepPolicy(true),
  tool: defaultStepPolicy(true),
  configurable: false,
};

export const CHANNEL_TARGET_OPTIONS = [
  {
    group: '渠道账号',
    options: [
      { value: 'credential.app_id', label: '应用 ID', dataType: 'string' },
      { value: 'credential.app_secret', label: '应用密钥', dataType: 'string' },
      { value: 'chat_policy.disable_p2p', label: '禁用单聊', dataType: 'boolean' },
      { value: 'chat_policy.disable_group', label: '禁用群聊', dataType: 'boolean' },
      { value: 'chat_policy.show_tool_calls', label: '展示工具调用详情', dataType: 'boolean' },
      {
        value: 'chat_policy.unknown_command_as_agent_input',
        label: '未命中斜杠指令透传 Agent',
        dataType: 'boolean',
      },
    ],
  },
];

export const CHANNEL_TARGET_TYPE_MAP: Record<string, string> = {};
CHANNEL_TARGET_OPTIONS.forEach((g) =>
  g.options.forEach((o) => {
    CHANNEL_TARGET_TYPE_MAP[o.value] = o.dataType;
  })
);

export interface ChannelFormConfig {
  channelType: ChannelType;
  bindingMode: ChannelAccountBindingMode;
  channelParamValues: Record<string, string>;
  isNew?: boolean;
}

export interface ChannelParamDef {
  name: string;
  target: string;
  type: string;
  label: string;
  defaultValue: string;
  options?: Array<{ value: string; label: string }>;
}

export const CHANNEL_PARAM_DEFINITIONS: Partial<Record<ChannelType, ChannelParamDef[]>> = {
  [ChannelType.FEISHU]: [
    {
      name: 'feishu_app_id',
      target: 'credential.app_id',
      type: 'string',
      label: '应用 ID',
      defaultValue: '',
    },
    {
      name: 'feishu_app_secret',
      target: 'credential.app_secret',
      type: 'string',
      label: '应用密钥',
      defaultValue: '',
    },
    {
      name: 'feishu_p2p_allow_from',
      target: 'chat_policy.p2p_allow_from',
      type: 'array',
      label: '私聊白名单',
      defaultValue: '[]',
    },
    {
      name: 'feishu_caller_user_id_field',
      target: 'chat_policy.caller_user_id_field',
      type: 'string',
      label: '调用者 ID 字段',
      defaultValue: '',
      options: [
        { value: '', label: '默认（渠道用户 ID）' },
        { value: 'open_id', label: 'Open ID' },
        { value: 'username', label: '用户名' },
        { value: 'email', label: '邮箱' },
        { value: 'phone', label: '手机号' },
      ],
    },
    {
      name: 'feishu_disable_p2p',
      target: 'chat_policy.disable_p2p',
      type: 'boolean',
      label: '禁用单聊',
      defaultValue: 'false',
    },
    {
      name: 'feishu_disable_group',
      target: 'chat_policy.disable_group',
      type: 'boolean',
      label: '禁用群聊',
      defaultValue: 'false',
    },
    {
      name: 'feishu_group_allow_from',
      target: 'chat_policy.group_allow_from',
      type: 'array',
      label: '群聊白名单',
      defaultValue: '[]',
    },
    {
      name: 'feishu_show_tool_calls',
      target: 'chat_policy.show_tool_calls',
      type: 'boolean',
      label: '展示工具调用详情',
      defaultValue: 'true',
    },
    {
      name: 'feishu_unknown_command_as_agent_input',
      target: 'chat_policy.unknown_command_as_agent_input',
      type: 'boolean',
      label: '未命中斜杠指令透传 Agent',
      defaultValue: 'false',
    },
  ],
  [ChannelType.QQ]: [
    {
      name: 'qq_app_id',
      target: 'credential.app_id',
      type: 'string',
      label: '应用 ID',
      defaultValue: '',
    },
    {
      name: 'qq_app_secret',
      target: 'credential.app_secret',
      type: 'string',
      label: '应用密钥',
      defaultValue: '',
    },
    {
      name: 'qq_p2p_allow_from',
      target: 'chat_policy.p2p_allow_from',
      type: 'array',
      label: '私聊白名单',
      defaultValue: '[]',
    },
    {
      name: 'qq_caller_user_id_field',
      target: 'chat_policy.caller_user_id_field',
      type: 'string',
      label: '调用者 ID 字段',
      defaultValue: '',
      options: [
        { value: '', label: '默认（渠道用户 ID）' },
        { value: 'open_id', label: 'Open ID' },
        { value: 'username', label: '用户名' },
        { value: 'email', label: '邮箱' },
        { value: 'phone', label: '手机号' },
      ],
    },
    {
      name: 'qq_unknown_command_as_agent_input',
      target: 'chat_policy.unknown_command_as_agent_input',
      type: 'boolean',
      label: '未命中斜杠指令透传 Agent',
      defaultValue: 'false',
    },
  ],
};

export interface PluginConfig {
  plugin_type: string;
  plugin_sub_type:
    | typeof PLUGIN_SUB_TYPE_CLASS
    | typeof PLUGIN_SUB_TYPE_INSTANCE
    | typeof PLUGIN_SUB_TYPE_TOOLKIT
    | typeof PLUGIN_SUB_TYPE_USER_TOOLKIT
    | typeof PLUGIN_SUB_TYPE_APPLICATION;
  plugin_id: string;
  plugin_params: ParamItem[];
  isNew?: boolean;
  selectedItemData?: {
    name: string;
    description?: string;
    type?: string;
    provider?: string;
  };
  isMicroApp?: boolean;
  memoryInteractionMode?: string;
  memoryMode?: 'mem0' | 'infini_memory';
  _originalPluginType?: string;
  _originalPluginId?: string;
  _originalPluginName?: string;
}

export interface ToolPermissionRuleFormState {
  toolName: string;
  argument: string;
  pattern: string;
}

export interface ToolPermissionsFormState {
  allow: ToolPermissionRuleFormState[];
  deny: ToolPermissionRuleFormState[];
  /** 无规则命中时的默认行为（HITL default_action）；0=未设，表单默认 REQUIRE_HUMAN */
  defaultAction: number;
  /** 触发人工审批时是否调用 LLM 生成一句中文意图描述（HITL enable_desc） */
  enableDesc: boolean;
  /** 生成意图描述使用的模型名，留空使用 vibe-agent 默认模型 */
  descModel: string;
}

/** filesystem middleware 工具分组：创建页默认全选，分组仅影响 UI 展示 */
export const FILESYSTEM_TOOL_GROUPS: {
  label: string;
  tools: { value: FilesystemTool; label: string; desc: string }[];
}[] = [
  {
    label: '读取',
    tools: [
      { value: FilesystemTool.LS, label: 'ls', desc: '列出目录内容' },
      { value: FilesystemTool.READ_FILE, label: 'read_file', desc: '读取文件' },
      { value: FilesystemTool.GLOB, label: 'glob', desc: '按模式匹配文件路径' },
      { value: FilesystemTool.GREP, label: 'grep', desc: '在文件内容中检索' },
    ],
  },
  {
    label: '写入',
    tools: [
      { value: FilesystemTool.WRITE_FILE, label: 'write_file', desc: '写入文件' },
      { value: FilesystemTool.EDIT_FILE, label: 'edit_file', desc: '编辑文件' },
      { value: FilesystemTool.DELETE, label: 'delete', desc: '删除文件/目录' },
    ],
  },
  {
    label: '执行',
    tools: [{ value: FilesystemTool.EXECUTE, label: 'execute', desc: '执行命令' }],
  },
];

export const FILESYSTEM_ALL_TOOLS: FilesystemTool[] = FILESYSTEM_TOOL_GROUPS.flatMap((g) =>
  g.tools.map((t) => t.value)
);

export const FILESYSTEM_READONLY_TOOLS: FilesystemTool[] = [
  FilesystemTool.LS,
  FilesystemTool.READ_FILE,
  FilesystemTool.GLOB,
  FilesystemTool.GREP,
];

export interface FormState {
  name: string;
  description: string;
  model_name: string;
  /** 编辑场景可填明文 Key；创建场景改用 api_key_ref */
  api_key: string;
  /** MaaS 凭证 ID，与 api_key 二选一，优先用于创建 Agent */
  api_key_ref: string;
  system_prompt: string;
  system_prompt_configurable: boolean;
  model_params: ParamItem[];
  extraConfig: ExtraConfigFormState;
  /** 启用 filesystem middleware；创建页默认勾选，存量 Agent 无字段时视为 false */
  enableFilesystem: boolean;
  /** filesystem middleware 启用的工具列表；仅 enableFilesystem 时生效，为空时由运行时使用默认工具集 */
  filesystemTools: FilesystemTool[];
  /** 启用 todo_list middleware；默认 false（不启用），存量 Agent 无字段时视为 false */
  todoListEnable: boolean;
  /** todo_list middleware 是否为 Todo 注入额外的 description 字段引导；仅 todoListEnable 时生效 */
  todoListEnableDesc: boolean;
  /** 启用 ask_user middleware（向用户提问的人工介入工具）；默认 false，存量 Agent 无字段时视为 false */
  askUserEnable: boolean;
  /** 启用 read_before_write middleware（写前必须读版本门控）；默认 false，存量 Agent 无字段时视为 false */
  readBeforeWriteEnable: boolean;
  /** 启用 web_search middleware（Tavily 联网搜索）；默认 false，存量 Agent 无字段时视为 false */
  webSearchEnable: boolean;
  /** Tavily API Key；webSearchEnable=true 时必填 */
  webSearchApiKey: string;
  /** 单次搜索最大结果数；0 表示由 framework 默认（5） */
  webSearchMaxResults: number;
  /** 启用 current_time middleware（每次模型调用注入当前时间）；存量 Agent 无字段时视为 false */
  currentTimeEnable: boolean;
  compression: CompressionFormState;
  faultTolerance: FaultToleranceFormState;
  toolPermissions: ToolPermissionsFormState;
  channelBindingMode: ChannelAccountBindingMode;
  channels: ChannelFormConfig[];
  plugins: PluginConfig[];
}

export interface AgentFormHandlers {
  addPlugin: (defaults?: {
    plugin_type?: string;
    plugin_sub_type?: PluginConfig['plugin_sub_type'];
  }) => void;
  removePlugin: (index: number) => void;
  updatePlugin: (index: number, field: keyof PluginConfig, value: unknown) => void;
  updatePluginParam: (
    pluginIndex: number,
    paramIndex: number,
    field: keyof ParamItem,
    value: string
  ) => void;
  fetchPluginInfo: (pluginIndex: number, pluginType: string, uid: string, subType?: string) => void;
  addChannel: () => void;
  removeChannel: (index: number) => void;
  updateChannel: (index: number, field: keyof ChannelFormConfig, value: unknown) => void;
  getDuplicateParamNames: () => Set<string>;
}

export interface AgentFormProps {
  form: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  handlers: AgentFormHandlers;
  showApiKey: boolean;
  setShowApiKey: (v: boolean | ((prev: boolean) => boolean)) => void;
  credentialPicker?: boolean;
  title: string;
  subtitle: string;
  submitLabel: string;
  isPending: boolean;
  agentLoading?: boolean;
  backTo: string;
  canSubmit: boolean;
  onSubmit: () => void;
}

export function useAgentFormData() {
  const sandboxList = useQuery({
    queryKey: ['sandbox-instances'],
    queryFn: () => sandboxApi.listSandboxes(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const sandboxClassesList = useQuery({
    queryKey: ['sandbox-classes'],
    queryFn: () =>
      sandboxClassApi.listSandboxClasses(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const skillGroupClassesList = useQuery({
    queryKey: ['skillgroup-classes', 'agent-form'],
    queryFn: () =>
      cortexApi.listSkillGroupClasses(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const skillGroupsList = useQuery({
    queryKey: ['skillgroups', 'agent-form'],
    queryFn: () => cortexApi.listSkillGroups(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const ragClassesList = useQuery({
    queryKey: ['rag-classes', 'agent-form'],
    queryFn: () =>
      ragPluginApi.listRAGClasses(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const ragsList = useQuery({
    queryKey: ['rags-plugin', 'agent-form'],
    queryFn: () => ragPluginApi.listRAGs(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const memoryList = useQuery({
    queryKey: ['memory-instances'],
    queryFn: () =>
      memoryApi.listMemories(
        create(ListRequestSchema, {
          page: 1,
          pageSize: 100,
        })
      ),
  });

  const memoryClassesList = useQuery({
    queryKey: ['memory-classes'],
    queryFn: () =>
      memoryClassApi.listMemoryClasses(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const toolkitList = useQuery({
    queryKey: ['internal-toolkits'],
    queryFn: () => agentApi.listInternalToolkits(),
  });

  const applicationList = useQuery({
    queryKey: ['applications', 'list', 'agent-form'],
    queryFn: () => applicationApi.list(create(ListRequestSchema, { page: 1, pageSize: 200 })),
  });

  const mcpList = useQuery({
    queryKey: ['mcps', 'agent-form'],
    queryFn: () => mcpApi.listMCPs(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  const mcpClassesList = useQuery({
    queryKey: ['mcp-classes', 'agent-form'],
    queryFn: () =>
      mcpClassApi.listMCPClasses(create(ListRequestSchema, { page: 1, pageSize: 100 })),
  });

  return {
    sandboxList,
    sandboxClassesList,
    skillGroupClassesList,
    skillGroupsList,
    ragClassesList,
    ragsList,
    memoryList,
    memoryClassesList,
    toolkitList,
    applicationList,
    mcpList,
    mcpClassesList,
  };
}
